import React, { useCallback, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';

import { reactToStatus, unreactToStatus } from '../actions/interactions';

const EMOJI_ORDER = [
  { name: 'thumbsup', emoji: '👍' },
  { name: 'heart',    emoji: '❤️' },
  { name: 'care',     emoji: '🤗' },
  { name: 'laugh',    emoji: '😂' },
  { name: 'wow',      emoji: '😮' },
  { name: 'sad',      emoji: '😢' },
  { name: 'angry',    emoji: '😡' },
];

interface Reaction {
  name: string;
  emoji: string;
  count: number;
  me: boolean;
}

interface Props {
  status: ImmutableMap<string, unknown>;
  myAccountId: string | null;
}

export const StatusReactions: React.FC<Props> = ({ status, myAccountId }) => {
  const dispatch = useDispatch();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rawReactions = status.get('reactions') as ImmutableList<ImmutableMap<string, unknown>> | undefined;

  const reactions: Reaction[] = rawReactions
    ? rawReactions.toArray().map((r) => ({
        name: r.get('name') as string,
        emoji: r.get('emoji') as string,
        count: r.get('count') as number,
        me: r.get('me') as boolean,
      }))
    : EMOJI_ORDER.map(e => ({ ...e, count: 0, me: false }));

  const handleReactionClick = useCallback((name: string, me: boolean) => {
    if (!myAccountId) return;
    if (me) {
      dispatch(unreactToStatus(status, name) as unknown as ReturnType<typeof unreactToStatus>);
    } else {
      dispatch(reactToStatus(status, name) as unknown as ReturnType<typeof reactToStatus>);
    }
    setPickerOpen(false);
  }, [dispatch, status, myAccountId]);

  const handleMouseEnter = useCallback(() => {
    if (!myAccountId) return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    openTimer.current = setTimeout(() => setPickerOpen(true), 250);
  }, [myAccountId]);

  const handleMouseLeave = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    // Small delay before closing so the mouse can cross the gap into the picker
    closeTimer.current = setTimeout(() => setPickerOpen(false), 250);
  }, []);

  const handleClick = useCallback(() => {
    if (!myAccountId) return;
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setPickerOpen(prev => !prev);
  }, [myAccountId]);

  const activeReactions = reactions.filter(r => r.count > 0);

  return (
    <div className='status-reactions'>
      {activeReactions.length > 0 && (
        <div className='status-reactions__bar'>
          {activeReactions.map(r => (
            <button
              key={r.name}
              className={`status-reactions__item${r.me ? ' status-reactions__item--me' : ''}`}
              onClick={() => handleReactionClick(r.name, r.me)}
              disabled={!myAccountId}
              title={r.name}
            >
              <span className='status-reactions__emoji'>{r.emoji}</span>
              {r.count > 0 && <span className='status-reactions__count'>{r.count}</span>}
            </button>
          ))}
        </div>
      )}

      {myAccountId && (
        <div
          className='status-reactions__picker-wrapper'
          ref={pickerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className='status-reactions__add-btn'
            onClick={handleClick}
            title='React'
          >
            <span>😊</span>
          </button>

          {pickerOpen && (
            <div
              className='status-reactions__picker'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {EMOJI_ORDER.map(({ name, emoji }) => (
                <button
                  key={name}
                  className='status-reactions__picker-item'
                  onClick={() => {
                    const reaction = reactions.find(r => r.name === name);
                    handleReactionClick(name, reaction?.me ?? false);
                  }}
                  title={name}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
